// RehabBudgetTab.tsx

import { View, ActivityIndicator } from "react-native"
import { H4, Small } from "@/components/standard/BFText"
import { SecondaryButton } from "@/components/buttons/CommonButtons"
import { Vertical } from "@/components/standard/BFLayout"
import {
  CONTINGENCY_CATEGORY,
  formatSelectedOptions,
} from "@/screens/rehab/utils/Rehab"
import { type RehabSowTabs } from "@/screens/rehab/RehabSowScreen"
import CategoryButton from "@/components/buttons/CategoryButton"
import { numberToCurrency } from "@/utils/NumberUtils"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { useRehabBudgetingFeature } from "@/hooks/features/useRehabBudgetingFeature"

interface RehabBudgetTabProps {
  setSelectedTab: (tab: RehabSowTabs) => void
  selectedCategory: string
  setSelectedCategory: (category: string) => void
}

export const RehabBudgetTab = ({
  setSelectedTab,
  selectedCategory,
  setSelectedCategory,
}: RehabBudgetTabProps) => {
  const { loanApplication } = useLoanContext()
  const isDesktop = useIsDesktop()

  // Fetch and normalize the rehab budget data
  const {
    lineItemsWithUserData,
    isLoading: isRehabBudgetLoading,
    isDetailsTabDisabled,
    rehabBudget: rehabData,
  } = useRehabBudgetingFeature(loanApplication, isDesktop)

  // Map the normalized line items to summary items for display.
  // Each item now includes the real budget value (defaulting to 0 if not set).
  const items = lineItemsWithUserData.map((item) => ({
    label: item.label,
    sublabel: formatSelectedOptions(item, item.userSelections),
    fieldName: item.fieldName,
    budget: numberToCurrency(item.budget),
    icon: item.icon,
  }))

  const onNext = () => {
    setSelectedTab("details")
  }

  if (!loanApplication) return null

  if (isRehabBudgetLoading) {
    return <ActivityIndicator />
  }

  return (
    <View className="flex flex-col">
      <Vertical className="gap-0 my-0">
        <H4 className="text-darkest mt-1 mb-2">Budget</H4>
        <Small>
          Provide the budget for each line item for the rehab of this property.
        </Small>
      </Vertical>

      <View className="my-4 gap-2">
        {items.map(({ label, sublabel, icon, fieldName, budget }, index) => (
          <CategoryButton
            key={index}
            label={label}
            sublabel={sublabel}
            icon={icon as any}
            selected={fieldName === selectedCategory}
            amount={budget}
            onPress={() => {
              setSelectedCategory(fieldName)
            }}
          />
        ))}
      </View>

      {/* Contingency */}
      <H4 className="text-darkest mt-3 mb-2">Contingency</H4>
      <CategoryButton
        label={CONTINGENCY_CATEGORY.Contingency.label}
        icon={CONTINGENCY_CATEGORY.Contingency.icon as any}
        selected={
          CONTINGENCY_CATEGORY.Contingency.fieldName === selectedCategory
        }
        onPress={() => {
          setSelectedCategory(CONTINGENCY_CATEGORY.Contingency.fieldName)
        }}
        contingencyType={{
          value: rehabData?.contingencyValue || "0",
          amount: rehabData?.contingencyAmount || "0",
        }}
        disabled={isDetailsTabDisabled}
      />

      <View className="mb-5 mt-8">
        <SecondaryButton
          testID="rehab-budget-next"
          loading={false}
          title="Next >"
          onPress={onNext}
          disabled={isDetailsTabDisabled}
        />
      </View>
    </View>
  )
}
